export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const API_ACCEPT_LANGUAGE = 'en';
export const API_TIMEOUT = 60000;
export const ACCESS_TOKEN_NAME = 'accessToken';
export const TOKEN_EXPIRATION_TIME = 'token_expires_at';
export const ACCESS_TOKEN_EXPIRATION_BUFFER = 30 * 1000; // 30 seconds in milliseconds
export const SIDE_NAVBAR_WIDTH = 304; // px
export const HEADER_HEIGHT = 68; // px
export const DEFAULT_LIST_LIMIT_FILTER = 20;
export const SEARCH_DEBOUNCE = 700;
export const DEFAULT_LIMIT = 20;

export const MAX_NUMBER_OF_CHURCH_IMAGES = 6;
export const CHURCH_IMAGE_MAX_SIZE_MB = 5;
export const CHURCH_IMAGE_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const MIN_CHURCH_IMAGE_WIDTH = 260;
export const MIN_CHURCH_IMAGE_HEIGHT = 260;

// Matches Assert\Image maxSize of 2M on POST /admin/church/{churchId}/prayers/{id}/image
export const PRAYER_IMAGE_MAX_SIZE_MB = 2;
export const PRAYER_IMAGE_MAX_SIZE_BYTES = 2 * 1024 * 1024;

export const STALE_TIME = 60 * 1000 * 5; // 5 minutes
export const GC_TIME = 60 * 1000 * 10; // 10 minutes

// Auto-close an impersonation session after this timeout
export const IMPERSONATION_INACTIVITY_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours
export const IMPERSONATION_INACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
export const IMPERSONATION_ACTIVITY_PERSIST_THROTTLE_MS = 30 * 1000; // 30 seconds

export const listsConfigDefaults = {
  staleTime: STALE_TIME,
  gcTime: GC_TIME,
  keepPreviousData: false,
  refetchOnWindowFocus: false,
  retry: 2,
};

export const NOTIFICATION_AUTO_CLOSE_TIME = 4000;

export const PHONE_NUMBER_PLACEHOLDER = '+1 (000) 000-0000 / +61 x xxxx xxxx';

enum PHONE_MASK_COUNTRY {
  US = 'US',
  AU = 'AU',
  RAW = 'RAW',
}

type PhoneCompiledMask = {
  country?: PHONE_MASK_COUNTRY;
};

/**
 * IMask dynamic masks for US (+1) and Australian (+61) numbers.
 * Spread PHONE_NUMBER_IMASK_PROPS onto IMaskInput / Mantine Input.
 * RAW is used until the country code is known so empty fields are not locked to +1.
 * AU international format is +61 plus exactly 9 digits (no trunk zero), grouped as +61 x xxxx xxxx.
 */
export const PHONE_NUMBER_MASK = [
  { mask: '+61 0 0000 0000', country: PHONE_MASK_COUNTRY.AU },
  { mask: '+1 (000) 000-0000', country: PHONE_MASK_COUNTRY.US },
  { mask: '+000000000000000', country: PHONE_MASK_COUNTRY.RAW },
];

const dispatchPhoneNumberMask = (
  appended: string,
  dynamicMasked: { rawInputValue?: string; compiledMasks: PhoneCompiledMask[] }
) => {
  const typedDigits = `${dynamicMasked.rawInputValue ?? ''}${appended}`.replace(/\D/g, '');

  if (typedDigits.startsWith('61')) {
    return dynamicMasked.compiledMasks.find((item) => item.country === PHONE_MASK_COUNTRY.AU);
  }

  // Lone "6" is ambiguous (AU +61 vs US area codes 60x/62x-69x) — wait for the next digit
  if (typedDigits === '6') {
    return dynamicMasked.compiledMasks.find((item) => item.country === PHONE_MASK_COUNTRY.RAW);
  }

  if (typedDigits.startsWith('1') || /^[2-9]/.test(typedDigits)) {
    return dynamicMasked.compiledMasks.find((item) => item.country === PHONE_MASK_COUNTRY.US);
  }

  return dynamicMasked.compiledMasks.find((item) => item.country === PHONE_MASK_COUNTRY.RAW);
};

/**
 * Props for react-imask dynamic phone masking.
 * Cast as any so Mantine Input accepts the spread while still passing `dispatch` at runtime.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PHONE_NUMBER_IMASK_PROPS: any = {
  mask: PHONE_NUMBER_MASK,
  dispatch: dispatchPhoneNumberMask,
};

export const APP_PREVIEW_URL = import.meta.env.VITE_APP_PREVIEW_URL;
export const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL;

export const SUPPORT_PHONE = '+ 1 (800) 994-9817';
export const SUPPORT_WEBSITE_LABEL = 'www.diocesan.com';
export const SUPPORT_WEBSITE_URL = 'https://diocesan.com';

export const MESSAGE_LINK_PLACEHOLDER = '[message-link]';

export const CHARACTERS_LIMIT_SMS_MESSAGE = 160;
export const CHARACTERS_LIMIT_FB_MESSAGE = 280;
// TODO: Twitter/X is temporarily disabled. This code may be deleted later.
// export const CHARACTERS_LIMIT_TWITTER_MESSAGE = 280;
